import React from "react";
import type { DateValue } from "react-aria";
import { useDateField, useLocale } from "react-aria";
import { useDateFieldState } from "react-stately";
import { createCalendar } from "@internationalized/date";
import type { AriaDateFieldOptions } from "@react-aria/datepicker";
import type { Props as FieldProps } from "../Form/Field";
import Field from "../Form/Field";
import { styled } from "@linaria/react";
import DateSegment from "./DateSegment";
import { color } from "..";
import { CloseCircleFill } from "../IconV2/react";

const DateFieldWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 7px 0px;
  padding-right: 8px;
  flex-grow: 1;

  &.disabled {
    opacity: 0.5;
  }
`;

const StyledField = styled(Field)`
  width: 100%;
`;

const AfterInputButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  margin-right: 8px;

  &.disabled {
    opacity: 0.5;
  }
`;

interface ClearButtonProps {
  hasSomeValue: boolean;
}

const ClearButton = styled.button<ClearButtonProps>`
  all: unset;
  align-items: center;
  background-color: ${color.hydrogen};
  color: ${color.titanium500};
  display: flex;
  height: 100%;

  transition: color 144ms linear;

  &:hover {
    cursor: pointer;
    color: ${color.titanium700};
  }

  display: ${({ hasSomeValue }) => (hasSomeValue ? "flex" : "none")};
`;

export interface DateFieldProps<T extends DateValue> extends FieldProps {
  dateOptions?: Partial<Omit<AriaDateFieldOptions<T>, "label" | "errorMessage">>;
  /** Whether to show the button that clears the selected date. */
  clearable?: boolean;
}

function DateField<T extends DateValue>({
  dateOptions,
  clearable = true,
  ...fieldProps
}: DateFieldProps<T>) {
  const { locale } = useLocale();

  const state = useDateFieldState({
    ...dateOptions,
    isDisabled: fieldProps.disabled,
    locale,
    createCalendar,
  });

  const ref = React.useRef(null);
  const { fieldProps: ariaFieldProps } = useDateField(dateOptions || {}, state, ref);

  const handleClear = React.useCallback(() => {
    for (const segment of state.segments) state.clearSegment(segment.type);
  }, [state]);

  const hasSomeValue = React.useMemo(
    () => state.segments.some(segment => segment.isEditable && !segment.isPlaceholder),
    [state],
  );

  const afterInputSlot = (
    <AfterInputButtonWrapper className={state.isDisabled ? "disabled" : ""}>
      {clearable && (
        <ClearButton type="button" hasSomeValue={hasSomeValue} onClick={handleClear}>
          <CloseCircleFill size="m" />
        </ClearButton>
      )}
      {fieldProps.afterInputSlot}
    </AfterInputButtonWrapper>
  );

  return (
    <StyledField {...fieldProps} afterInputSlot={afterInputSlot}>
      <DateFieldWrapper
        {...ariaFieldProps}
        ref={ref}
        className={state.isDisabled ? "disabled" : ""}
      >
        {state.segments.map((segment, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <DateSegment key={i} segment={segment} state={state} />
        ))}
        {/* {state.isInvalid && <span aria-hidden="true">🚫</span>} */}
      </DateFieldWrapper>
    </StyledField>
  );
}

export default DateField;
